import React, { useState, useEffect } from 'react';
import { components } from 'react-select';
import { Image } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';
import deleteIcon from './../../assets/images/button-icons/trash-icon.png';
import { BiLayerPlus } from "react-icons/bi";
import { ConfirmModal, AdminSelect } from '@project/components';
import styles from './ProfileFloors.module.css';
import axios from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import spaceProfileDefaults, { setDefaults } from '../SpaceProfile/defaults';
import ChangeFloorName from '../ChangeFloorName/ChangeFloorName';
import { useIntl } from 'react-intl';
import { FLOOR_LABELS } from '../../constants';

const classNames = require('classnames');
function ProfileFloors({ spaceProfile, floorData, setErrors, setFloorData, saveProfile, isReadOnly, floorErrors, setFloorErrors }) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteLevel, setDeleteLevel] = useState({});

  const currentFloor = spaceProfile.floor || 1;

  useEffect(() => {
    setIsSaving(false);
  }, [currentFloor]);

  const onAddLevel = async (e) => {
    // make a post request to space profile endpoint creating a new sp
    e.preventDefault();
    if (isSaving) return;

    try {
      setIsSaving(true);
      setShowConfirmModal(false);

      const newFloor = floorData && floorData.length ? Math.max(...floorData.map(_ => parseInt(_.floor))) + 1 : 2;

      const newRecord = {
        company: spaceProfile.company,
        property: spaceProfile.property,
        address1: spaceProfile.address1,
        address2: spaceProfile.address2,
        city: spaceProfile.city,
        state: spaceProfile.state,
        postal: spaceProfile.postal,
        asset_guid: spaceProfile.asset_guid,
        latitude: spaceProfile.latitude,
        longitude: spaceProfile.longitude,
        basic_info: {},
        parent_guid: spaceProfile.parent_guid || spaceProfile.guid,
        floor: newFloor,
        floor_label: `Level ${newFloor}`
      };

      setDefaults(spaceProfileDefaults, newRecord);
      const result = await axios.post('/space-profiles', newRecord);

      if (result.status === 200) {
        setIsSaving(false);
        navigate(`/admin/profile/edit/${result.data.record.guid}`);
      } else {
        setErrors(`${t('error.general')}: ${result.errors}`);
      }

      setFloorErrors(prevState => ({...prevState}))
    } catch (e) {
      setIsSaving(false);
      if (e.response && e.response.data) {
        setErrors(`${t('error.addNewLevel')} (${e.response.data.errors})`);
      } else {
        setErrors(t('error.server'));
      }
    }

  }

  const onDeleteLevel = () => {
    if (isSaving) return;
    setIsSaving(true);
    setShowConfirmDeleteModal(false);
    axios.delete(`/space-profiles/${deleteLevel.guid}`, {
      params: {
        company: spaceProfile.company
      }
    }).then(result => {
      if (result.status === 200) {
        setIsSaving(false);
        setFloorData(floorData.filter(_ => parseInt(_.floor) !== parseInt(deleteLevel.floor)))
        // if the level we're deleting is the current one, then redirect to the main profile
        if (parseInt(currentFloor) === parseInt(deleteLevel.floor)) {
          navigate(`/admin/profile/edit/${spaceProfile.parent_guid}`);
        }

        // Remove current level from floor errors array to keep state in sync
        delete floorErrors[deleteLevel.floor];
        setFloorErrors(floorErrors);
      } else {
        setErrors(`${t('error.general')}: ${result.errors}`);
      }
    }).catch((error) => {
      setErrors(`${t('error.deleteNewLevel')} (${error.response.data.errors})`);
    }).finally(() => setIsSaving(false));
  };

  const onChangeLevel = ({ guid }) => {
    if (spaceProfile.guid === guid) return;
    setIsSaving(true);
    setErrors(null)
    navigate(`/admin/profile/edit/${guid}`);
  }

  const onClickDelete = (level) => {
    setDeleteLevel(level);
    setShowConfirmDeleteModal(true);
  }


  const Option = props => {
    const { innerProps, innerRef } = props;
    return (
      <div className={classNames(styles.selectOption, 'p-2')}>
        <div className="optionLabel" ref={innerRef} {...innerProps}>{props.label}</div>
        {props.value !== 1 && !isReadOnly ? <div onClick={() => onClickDelete(props.data)}><Image src={deleteIcon} className="mr-2" /></div> : null}
      </div>
    );
  };

  // Create a Menu component to replace the stock version in react-select with one adding a link
  const Menu = props =>
    <components.Menu {...props}>
      {props.children}
    </components.Menu>

  let options = floorData.map(_ => ({ ..._, label: FLOOR_LABELS[_.floor_label] || (_.floor_label && intl.formatMessage({ id: 'floorLabels.level' }, { level: _.floor_label.split(' ')[1] })) || intl.formatMessage({ id: 'floorLabels.level' }, { level: _.floor }), value: _.floor })).sort((a, b) => a.value - b.value);
  if (!floorData.length) options = [{ value: 1, label: intl.formatMessage({ id: 'floorLabels.level' }, { level: 1 }) }];

  // the current value to display in the switch floors dropdown
  const currentValue = { value: currentFloor, label: FLOOR_LABELS[spaceProfile.floor_label] || (spaceProfile.floor_label && intl.formatMessage({ id: 'floorLabels.level' }, { level: spaceProfile.floor_label.split(' ')[1] })) || intl.formatMessage({ id: 'floorLabels.level' }, { level: currentFloor }), guid: spaceProfile.guid };

  return (
    <div className={styles.mainContainer}>
      <AdminSelect
        id="switchFloors"
        label=""
        options={options}
        value={currentValue}
        customComponents={{ Menu, Option }}
        onChange={onChangeLevel}
        isDisabled={!!(options.length < 2)} />
      {isSaving ? <Spinner className="m-4" animation="grow" variant="primary" /> : null}
      {!isReadOnly ? <div className={`${styles.addLayer} data-add-level`} onClick={() => setShowConfirmModal(true)}><BiLayerPlus /></div> : null}
      <ChangeFloorName
        spaceProfile={spaceProfile}
        isReadOnly={isReadOnly}
        currentFloor={currentFloor}
        saveProfile={saveProfile}
        floorData={floorData}
        setFloorData={setFloorData} />
      {/* <Button variant="light" className={'mt-2 rounded-0 border ml-3 '} onClick={() => setShowFloorNameModal(true)} disabled={isSaving}>Change Floor Name</Button> */}
      <ConfirmModal show={showConfirmModal} title={t('ProfileFloors.addNewLevel.title')} onConfirm={onAddLevel} onHide={() => setShowConfirmModal(false)} confirmButtonText={t('ProfileFloors.addNewLevel.confirm')} cancelButtonText={t('ui.cancel')}>
        {t('ProfileFloors.addNewLevel.details')}
      </ConfirmModal>
      <ConfirmModal show={showConfirmDeleteModal} title={t('ProfileFloors.removeLevel.title')} onConfirm={onDeleteLevel} onHide={() => setShowConfirmDeleteModal(false)} confirmButtonText={t('ProfileFloors.removeLevel.confirm')} cancelButtonText={t('ui.cancel')}>
        {intl.formatMessage({ id: 'ProfileFloors.removeLevel.details' }, { level: FLOOR_LABELS[deleteLevel.floor_label] || (deleteLevel.floor_label && intl.formatMessage({ id: 'floorLabels.level' }, { level: deleteLevel.floor_label.split(' ')[1] })) })}
      </ConfirmModal>
    </div>
  )
}

ProfileFloors.propTypes = {

}

export default ProfileFloors

